import { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Building2, Menu, X } from "lucide-react";

const navLinks = [
  { label: "How it works", href: "/how-it-works" },
  { label: "Why us", href: "/why-us" },
  { label: "Stories", href: "/stories" },
  { label: "Calculator", href: "/calculator" },
  { label: "About us", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export default function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (href: string) => location.pathname === href;
  const onLogin = location.pathname === "/login";
  const onRegister = location.pathname === "/register";
  const onAuthPage = onLogin || onRegister;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-foreground relative overflow-hidden">
      {/* Ambient background */}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 h-[600px] w-[900px] rounded-full bg-primary/[0.04] blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[400px] w-[600px] rounded-full bg-emerald-500/[0.03] blur-3xl" />

      <div className="relative flex min-h-screen flex-col">
        {/* ── Sticky Header ── */}
        <header className="sticky top-0 z-50 border-b border-border/40 bg-white/80 backdrop-blur-lg">
          <div className="container mx-auto flex h-14 items-center justify-between px-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-foreground text-background">
                <Building2 className="h-4 w-4" />
              </div>
              <span className="text-base font-semibold font-display">
                Ngwees Capital
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden items-center gap-1 md:flex">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                    isActive(link.href)
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              {!onLogin && (
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="hidden text-muted-foreground hover:text-foreground sm:inline-flex"
                >
                  <Link to="/login">Sign in</Link>
                </Button>
              )}
              {!onRegister && (
                <Button asChild size="sm">
                  <Link to="/register">
                    {onLogin ? "Create account" : "Get started"}
                    <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Link>
                </Button>
              )}
              {onRegister && (
                <Button asChild size="sm" variant="outline">
                  <Link to="/login">Sign in</Link>
                </Button>
              )}

              {/* Mobile menu toggle */}
              <button
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted md:hidden"
                onClick={() => setMobileMenuOpen((o) => !o)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile nav drawer */}
          {mobileMenuOpen && (
            <div className="border-t border-border/40 bg-white px-6 py-4 md:hidden">
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`rounded-lg px-3 py-2 text-sm transition-colors ${
                      isActive(link.href)
                        ? "bg-muted font-medium text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/5"
                >
                  Sign in
                </Link>
              </nav>
            </div>
          )}
        </header>

        {/* ── Page content ── */}
        <main className="flex-1">
          <Outlet />
        </main>

        {/* ── Footer ── */}
        <footer className="border-t border-border/40 bg-white/60">
          <div className="container mx-auto px-6 py-8">
            <div className="flex flex-col items-center gap-4 text-center text-sm text-muted-foreground sm:flex-row sm:justify-between sm:text-left">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span className="font-semibold text-foreground">
                  Ngwees Capital
                </span>
              </div>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/admin/login"
                  className="hover:text-foreground transition-colors"
                >
                  Staff login
                </Link>
                <a
                  href="mailto:support@bpcashsolutions.com"
                  className="hover:text-foreground transition-colors"
                >
                  support@bpcashsolutions.com
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
